import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, Chip, Stack,
  CircularProgress, Alert, Avatar, Divider,
} from '@mui/material';
import {
  ScubaDiving, Straighten, Schedule, Place, Thermostat, InfoOutlined, Check,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import { diveShareService, SharePreview } from '../services/diveShareService';
import { authService } from '../services/authService';
import { formatDepth, formatTemperature } from '../utils/unitConversions';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function durationLabel(iso: string): string {
  const parts = iso?.split(':');
  if (!parts || parts.length < 2) return '';
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  return `${h * 60 + m} min`;
}

/**
 * Landing page for a dive share link. Signed-out visitors see the dive and a sign-in
 * prompt rather than a dead end — they keep the link's context through auth.
 */
const DiveSharedClaim: React.FC = () => {
  const { shareCode } = useParams<{ shareCode: string }>();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<SharePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [signedIn, setSignedIn] = useState(authService.isAuthenticated());

  const load = useCallback(async () => {
    if (!shareCode) {
      setError('This share link is not valid');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setPreview(await diveShareService.previewByCode(shareCode));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'This share link is not valid');
    } finally {
      setLoading(false);
    }
  }, [shareCode]);

  useEffect(() => { load(); }, [load]);

  const handleClaim = async () => {
    if (!shareCode) return;
    setClaiming(true);
    setError(null);
    try {
      const dive = await diveShareService.claimByCode(shareCode);
      navigate(`/dive/${dive.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add this dive');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Box>
      <AppNavigation />
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error && !preview ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {error}
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => navigate('/explore')}>
                Explore dive sites instead
              </Button>
            </Box>
          </Alert>
        ) : preview ? (
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ScubaDiving sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                A dive was shared with you
              </Typography>
            </Box>

            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontSize: '0.9rem' }}>
                    {initials(preview.ownerDisplayName) || '?'}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                      {preview.ownerDisplayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {preview.diveLabel}
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    icon={<Straighten fontSize="small" />}
                    label={`Max ${formatDepth(preview.snapshot.maxDepth, 'meters')}`}
                  />
                  <Chip
                    size="small"
                    icon={<Schedule fontSize="small" />}
                    label={durationLabel(preview.snapshot.duration)}
                  />
                  {preview.snapshot.waterTemperature > 0 && (
                    <Chip
                      size="small"
                      icon={<Thermostat fontSize="small" />}
                      label={formatTemperature(preview.snapshot.waterTemperature, 'celsius')}
                    />
                  )}
                  {preview.snapshot.location && (
                    <Chip size="small" icon={<Place fontSize="small" />} label={preview.snapshot.location} />
                  )}
                  {preview.snapshot.diveProfile?.length > 0 && (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`Depth profile · ${preview.snapshot.diveProfile.length} points`}
                    />
                  )}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
                  <Typography variant="caption" color="text.secondary">
                    Adding this puts it in your log as your own dive. Depth profile, times and
                    conditions come across — you fill in your air, weights and gear.
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {preview.alreadyClaimed ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                This share link has already been used.
              </Alert>
            ) : signedIn ? (
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 2.5 }}
                onClick={handleClaim}
                disabled={claiming}
                startIcon={claiming ? <CircularProgress size={16} color="inherit" /> : <Check />}
              >
                Add to my dive log
              </Button>
            ) : (
              <SignInNudge
                feature="add this dive to your log"
                description="Sign in or create a free account, and this dive is waiting for you."
                onSignedIn={() => setSignedIn(true)}
              />
            )}
          </>
        ) : null}
      </Container>
    </Box>
  );
};

export default DiveSharedClaim;
